import type { DataBuilderRawResponse } from '../api/columns-api-types';
import type { DataBuilderModel } from '../api/data-builder-api.types';
import type { DeliverableColumnConfig } from './deliverable-column-config';
import { buildDeliverablesConfig } from './deliverable-column-config';
import {
  findFirstArray,
  getBoolean,
  getString,
  isRecord,
  slugify,
  toTitleCase,
} from './parse-utils';

// ── App-level types ────────────────────────────────────────────────

export type DataBuilderDataset = {
  id: string;
  label: string;
  detail?: string;
  isDisabled?: boolean;
  groupId: string;
};

export type DataBuilderGroup = {
  id: string;
  name: string;
};

export type { DeliverableColumnConfig } from './deliverable-column-config';

export type DataBuilderResultsTransformed = {
  datasets: DataBuilderDataset[];
  groups: DataBuilderGroup[];
  deliverablesConfig: Record<string, DeliverableColumnConfig>;
  models: DataBuilderModel[];
};

// ── Constants ──────────────────────────────────────────────────────

/** Types that represent structural wrappers, not selectable datasets. */
const STRUCTURAL_TYPES = new Set(['deliverable', 'group']);

// ── Entry parsing ──────────────────────────────────────────────────

type ParsedEntry = {
  id: string;
  label: string;
  detail?: string;
  isDisabled?: boolean;
  children?: ParsedEntry[];
};

const resolveEntryId = (
  obj: Record<string, unknown>,
  fallbackLabel: string | undefined
): string | undefined => {
  const rawType = getString(obj.type);
  const typeAsId =
    rawType && !STRUCTURAL_TYPES.has(rawType) ? rawType : undefined;
  const explicit = getString(obj.key) ?? getString(obj.id) ?? typeAsId;
  if (explicit) {
    return explicit;
  }
  if (fallbackLabel) {
    return slugify(fallbackLabel);
  }
  return undefined;
};

const parseStringEntry = (value: string): ParsedEntry => ({
  id: value,
  label: toTitleCase(value),
});

const parseObjectEntry = (obj: Record<string, unknown>): ParsedEntry | null => {
  const rawLabel =
    getString(obj.name) ?? getString(obj.label) ?? getString(obj.title);
  const id = resolveEntryId(obj, rawLabel);

  if (!id) {
    return null;
  }

  const label = rawLabel ?? toTitleCase(id);
  const detail = getString(obj.detail) ?? getString(obj.description);
  const isDisabled = getBoolean(obj.isDisabled) ?? getBoolean(obj.disabled);

  const childrenArray = findFirstArray(obj, [
    'deliverables',
    'items',
    'children',
    'options',
  ]);

  if (childrenArray?.length) {
    const parsedChildren = childrenArray
      .map(parseEntry)
      .filter(
        (entry): entry is ParsedEntry => entry !== null && !entry.children
      );

    if (parsedChildren.length) {
      return { id, label, detail, isDisabled, children: parsedChildren };
    }
  }

  return { id, label, detail, isDisabled };
};

const parseEntry = (value: unknown): ParsedEntry | null => {
  if (typeof value === 'string') {
    return parseStringEntry(value);
  }
  if (isRecord(value)) {
    return parseObjectEntry(value);
  }
  return null;
};

// ── Main transform ─────────────────────────────────────────────────

const toDataset = (
  entry: ParsedEntry,
  groupId: string
): DataBuilderDataset => ({
  id: entry.id,
  label: entry.label,
  detail: entry.detail,
  isDisabled: entry.isDisabled,
  groupId,
});

export const transformDataBuilderResults = (
  raw: unknown
): DataBuilderResultsTransformed => {
  const groups: DataBuilderGroup[] = [];
  const datasets: DataBuilderDataset[] = [];
  const models = Array.isArray(
    (raw as DataBuilderRawResponse | undefined)?.models
  )
    ? ((raw as DataBuilderRawResponse).models ?? [])
    : [];

  if (!isRecord(raw)) {
    return { datasets, groups, deliverablesConfig: {}, models: [] };
  }

  const entries = findFirstArray(raw, [
    'datasets',
    'deliverables',
    'pipelines',
  ]);
  if (!entries) {
    return { datasets, groups, deliverablesConfig: {}, models };
  }

  for (const entry of entries) {
    const parsed = parseEntry(entry);
    if (!parsed) {
      continue;
    }

    if (parsed.children?.length) {
      groups.push({ id: parsed.id, name: parsed.label });
      for (const child of parsed.children) {
        datasets.push(toDataset(child, parsed.id));
      }
    }
  }

  const deliverablesConfig = buildDeliverablesConfig(
    raw as DataBuilderRawResponse
  );

  return { datasets, groups, deliverablesConfig, models };
};

// ── Helpers ────────────────────────────────────────────────────────

export const groupDatasetsByGroupId = (
  datasets: DataBuilderDataset[]
): Map<string, DataBuilderDataset[]> => {
  const byGroup = new Map<string, DataBuilderDataset[]>();
  for (const ds of datasets) {
    const list = byGroup.get(ds.groupId) ?? [];
    list.push(ds);
    byGroup.set(ds.groupId, list);
  }
  return byGroup;
};
